import { PayloadAction, createSlice } from "@reduxjs/toolkit";

type InitialState = {
    scale: {
        value: number;
        mode: "fill" | "fit" | "custom";
    };
    translate: {
        x: {
            clientX: number | null;
            translateX: number;
        };
        y: {
            clientY: number | null;
            translateY: number;
        };
    };
    deviceSkin: boolean;
    zoomDisabled: boolean;
};

const initialState: InitialState = {
    scale: {
        value: 1,
        mode: "fit"
    },
    translate: {
        x: {
            clientX: null,
            translateX: 0
        },
        y: {
            clientY: null,
            translateY: 0
        }
    },
    deviceSkin: false,
    zoomDisabled: false
};

const frameTransformSlice = createSlice({
    name: "frameTransform",
    initialState: initialState,
    reducers: {
        //Scale
        scaleIncrementUp: state => {
            state.scale.mode = "custom";
            state.scale.value = state.scale.value + 0.05;
        },
        scaleIncrementDown: state => {
            state.scale.mode = "custom";
            if (state.scale.value > 0.1) {
                state.scale.value = state.scale.value - 0.05;
            }
        },
        setScale: (state, action: PayloadAction<number>) => {
            state.scale.value = action.payload;
        },
        setScaleMode: (
            state,
            action: PayloadAction<
                | "fill"
                | "fit"
                | "custom"
                | {
                      mode: "fill" | "fit" | "custom";
                      translateX?: number;
                      translateY?: number;
                  }
            >
        ) => {
            const mode = typeof action.payload === "string" ? action.payload : action.payload.mode;
            const translateX = typeof action.payload === "object" ? action.payload.translateX ?? 0 : 0;
            const translateY = typeof action.payload === "object" ? action.payload.translateY ?? 0 : 0;

            if (mode === "fill" || mode === "fit") {
                state.translate = {
                    x: {
                        clientX: null,
                        translateX: translateX
                    },
                    y: {
                        clientY: null,
                        translateY: translateY
                    }
                };
            }
            state.scale.mode = mode;
        },
        //Translate
        updateTranslate: (state, action: PayloadAction<{ clientX?: number | null; clientY?: number | null; translateX?: number; translateY?: number }>) => {
            if (state.scale.mode !== "custom") {
                state.scale.mode = "custom";
            }

            // If translateX/translateY are provided directly, use them
            if (action.payload.translateX !== undefined) {
                state.translate.x.translateX = action.payload.translateX;
            }
            if (action.payload.translateY !== undefined) {
                state.translate.y.translateY = action.payload.translateY;
            }

            const clientX = action.payload.clientX;
            if (clientX !== undefined) {
                if (clientX !== null) {
                    if (state.translate.x.clientX !== null && state.translate.x.clientX !== undefined) {
                        // Legacy behavior: calculate delta from clientX
                        state.translate.x.translateX =
                            state.translate.x.translateX + Math.round((clientX - state.translate.x.clientX) / state.scale.value);
                    }
                    state.translate.x.clientX = clientX;
                } else {
                    state.translate.x.clientX = null;
                }
            }

            const clientY = action.payload.clientY;
            if (clientY !== undefined) {
                if (clientY !== null) {
                    if (state.translate.y.clientY !== null && state.translate.y.clientY !== undefined) {
                        // Legacy behavior: calculate delta from clientY
                        state.translate.y.translateY =
                            state.translate.y.translateY + Math.round((clientY - state.translate.y.clientY) / state.scale.value);
                    }
                    state.translate.y.clientY = clientY;
                } else {
                    state.translate.y.clientY = null;
                }
            }
        },
        //Device Skin
        setDeviceSkin: (state, action: PayloadAction<boolean>) => {
            state.deviceSkin = action.payload;
        },
        //Zoom Control
        setZoomDisabled: (state, action: PayloadAction<boolean>) => {
            state.zoomDisabled = action.payload;
        }
    }
});

export default frameTransformSlice.reducer;
export const { scaleIncrementUp, scaleIncrementDown, setScale, setScaleMode, updateTranslate, setDeviceSkin, setZoomDisabled } = frameTransformSlice.actions;
